<script setup lang="ts">
    const props = defineProps<{
        error: {
            status?: number;
            statusCode?: number;
        };
    }>();

    defineOptions({ name: 'ErrorPage' });

    const { t, te, locale, availableLocales, setLocale } = useI18n();
    const { lang, urlSegment } = useLanguage();
    const runtimeConfig = useRuntimeConfig();

    // error.vue renders outside app.vue, which normally initializes the active locale.
    const errorLocale = availableLocales.find((availableLocale) => availableLocale === lang.value);
    if (errorLocale && locale.value !== errorLocale) {
        await setLocale(errorLocale);
    }

    useSeoMeta({
        title: runtimeConfig.public.defaultTitleSuffix
    });

    const fallbackMessages = {
        error: 'Error',
        errorGeneral: 'Something went wrong.',
        error404: 'The page you are looking for could not be found.',
        error404Link: 'Back to homepage',
        toHome: 'Go to homepage'
    };

    const translateWithFallback = (key: string, fallback: string) => {
        return te(key) ? t(key) : fallback;
    };

    const errorMessageMap = computed<Record<number, string>>(() => ({
        404: translateWithFallback('errorPages.404', fallbackMessages.error404),
        408: translateWithFallback('errorPages.error-general', fallbackMessages.errorGeneral),
        500: translateWithFallback('errorPages.error-general', fallbackMessages.errorGeneral),
        502: translateWithFallback('errorPages.error-general', fallbackMessages.errorGeneral),
        503: translateWithFallback('errorPages.error-general', fallbackMessages.errorGeneral)
    }));

    const statusCode = computed(() => props.error.statusCode ?? props.error.status);
    const errMessage = computed(() => (statusCode.value ? errorMessageMap.value[statusCode.value] : undefined));
    const { showDraftModeToggle } = useDraftMode();

    onMounted(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
</script>

<template>
    <LayoutHeader />

    <div class="error-container">
        <div class="image-wrapper">
            <img
                src="~/assets/images/404.svg"
                alt=""
            />
        </div>

        <div>
            <div>
                <h1>
                    <span class="sr-only"> {{ translateWithFallback('error', fallbackMessages.error) }} </span
                    >{{ statusCode }}
                </h1>
                <p
                    v-if="errMessage"
                    class="error-message"
                >
                    {{ errMessage }}
                    <a :href="`/${urlSegment}`">
                        {{ translateWithFallback('errorPages.404-link', fallbackMessages.error404Link) }}</a
                    >
                </p>
                <div class="button-wrapper">
                    <PrimaryButton
                        :button-text="translateWithFallback('general.toHome', fallbackMessages.toHome)"
                        :button-href="`/${urlSegment}`"
                    />
                </div>
            </div>
        </div>
    </div>

    <LayoutFooter />
    <DraftModeToggle v-if="showDraftModeToggle" />
</template>

<style>
    @import '~/assets/scss/navigation/error.scss';
</style>
